(() => {
  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
  let lastRewardProgress = null;
  let giftRequestPending = false;

  function syncDrawerEmptyState() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const isEmpty = Boolean(drawer.querySelector('.drawer__inner-empty'));
    drawer.classList.toggle('is-empty', isEmpty);
    drawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
  }

  customElements.whenDefined('cart-drawer').then(() => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    syncDrawerEmptyState();
    new MutationObserver(syncDrawerEmptyState).observe(drawer, {
      childList: true,
      subtree: true,
    });
  });

  async function updateCart(url, formData) {
    await customElements.whenDefined('cart-drawer');

    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(url, config);
    const state = await response.json();

    if (!response.ok || state.status) {
      throw new Error(state.description || state.message || 'Cart could not be updated.');
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      window.location.assign(window.routes?.cart_url || '/cart');
      return state;
    }

    drawer.renderContents(state);
    syncDrawerEmptyState();
    return state;
  }

  function setBusy(element, busy) {
    element.toggleAttribute('aria-busy', busy);
    element.querySelectorAll('button, input, select').forEach((control) => {
      control.disabled = busy || control.dataset.wasDisabled === 'true';
    });
  }

  function showError(element, message = '') {
    const error = element.querySelector('[data-cart-extra-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  if (!customElements.get('cart-guarantee-carousel')) {
    customElements.define(
      'cart-guarantee-carousel',
      class CartGuaranteeCarousel extends HTMLElement {
        connectedCallback() {
          if (this.dataset.initialized === 'true') return;
          this.dataset.initialized = 'true';
          this.slides = [...this.querySelectorAll('[data-guarantee-slide]')];
          this.index = Math.max(
            0,
            this.slides.findIndex((slide) => slide.classList.contains('is-active')),
          );
          this.prevButton = this.querySelector('[data-guarantee-prev]');
          this.nextButton = this.querySelector('[data-guarantee-next]');
          this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.drawer = this.closest('cart-drawer');
          this.handleActivityChange = () => {
            if (document.hidden || (this.drawer && !this.drawer.classList.contains('active'))) {
              this.stop();
            } else {
              this.start();
            }
          };

          const hasMultipleSlides = this.slides.length > 1;
          this.prevButton?.toggleAttribute('hidden', !hasMultipleSlides);
          this.nextButton?.toggleAttribute('hidden', !hasMultipleSlides);
          this.prevButton?.addEventListener('click', () => this.show(this.index - 1, true));
          this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
          this.addEventListener('pointerenter', () => this.stop());
          this.addEventListener('pointerleave', () => this.start());
          this.addEventListener('focusin', () => this.stop());
          this.addEventListener('focusout', () => this.start());
          document.addEventListener('visibilitychange', this.handleActivityChange);
          if (this.drawer) {
            this.drawerObserver = new MutationObserver(this.handleActivityChange);
            this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
          }
          this.show(this.index);
          this.handleActivityChange();
        }

        disconnectedCallback() {
          this.stop();
          this.drawerObserver?.disconnect();
          document.removeEventListener('visibilitychange', this.handleActivityChange);
        }

        show(nextIndex, restart = false) {
          if (!this.slides.length) return;
          this.index = (nextIndex + this.slides.length) % this.slides.length;
          this.slides.forEach((slide, index) => {
            const active = index === this.index;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', String(!active));
          });
          if (restart) {
            this.stop();
            this.start();
          }
        }

        start() {
          if (
            this.reducedMotion ||
            this.slides.length < 2 ||
            this.timer ||
            document.hidden ||
            (this.drawer && !this.drawer.classList.contains('active'))
          ) {
            return;
          }
          this.timer = window.setInterval(() => this.show(this.index + 1), 4200);
        }

        stop() {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      },
    );
  }

  if (!customElements.get('cart-rewards-progress')) {
    customElements.define(
      'cart-rewards-progress',
      class CartRewardsProgress extends HTMLElement {
        connectedCallback() {
          const track = this.querySelector('.ll-cart-rewards__track');
          if (!track) return;

          const target = Math.max(0, Math.min(100, Number(track.dataset.progress) || 0));
          const start = lastRewardProgress === null ? 0 : lastRewardProgress;
          track.style.setProperty('--ll-cart-progress', `${start}%`);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              track.style.setProperty('--ll-cart-progress', `${target}%`);
              lastRewardProgress = target;
            });
          });
        }
      },
    );
  }

  if (!customElements.get('cart-gift-carousel')) {
    customElements.define(
      'cart-gift-carousel',
      class CartGiftCarousel extends HTMLElement {
        connectedCallback() {
          if (this.dataset.initialized === 'true') return;
          this.dataset.initialized = 'true';

          this.track = this.querySelector('[data-gift-track]');
          this.slides = [...this.querySelectorAll('[data-gift-slide]')];
          this.previousButton = this.querySelector('[data-gift-prev]');
          this.nextButton = this.querySelector('[data-gift-next]');
          this.currentLabel = this.querySelector('[data-gift-current]');
          this.currentIndex = 0;

          this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
          this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
          this.goTo(0);
        }

        goTo(index) {
          if (!this.track || this.slides.length === 0) return;
          this.currentIndex = Math.max(0, Math.min(this.slides.length - 1, index));
          this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;

          this.slides.forEach((slide, slideIndex) => {
            const isCurrent = slideIndex === this.currentIndex;
            slide.toggleAttribute('inert', !isCurrent);
            slide.setAttribute('aria-hidden', String(!isCurrent));
          });

          if (this.currentLabel) this.currentLabel.textContent = String(this.currentIndex + 1);
          if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
          if (this.nextButton) this.nextButton.disabled = this.currentIndex === this.slides.length - 1;
        }
      },
    );
  }

  if (!customElements.get('cart-gift-picker')) {
    customElements.define(
      'cart-gift-picker',
      class CartGiftPicker extends HTMLElement {
        connectedCallback() {
          if (this.dataset.initialized === 'true') return;
          this.dataset.initialized = 'true';

          this.button = this.querySelector('.ll-cart-gift__add');
          this.variantInput = this.querySelector('[data-gift-variant-id]');
          this.image = this.querySelector('[data-gift-image]');
          this.selects = [...this.querySelectorAll('[data-gift-option]')];

          try {
            this.variants = JSON.parse(this.querySelector('[data-gift-variants]')?.textContent || '[]');
          } catch {
            this.variants = [];
          }

          this.selects.forEach((select) =>
            select.addEventListener('change', (event) => {
              event.stopPropagation();
              this.updateVariant();
            }),
          );
          this.button?.addEventListener('click', (event) => this.addGift(event));
          this.updateVariant();
        }

        updateVariant() {
          const selectedOptions = this.selects.map((select) => select.value);
          const variant = this.variants.find(
            (candidate) =>
              candidate.options.length === selectedOptions.length &&
              candidate.options.every((option, index) => option === selectedOptions[index]),
          );

          if (variant) {
            this.variantInput.value = variant.id;
            if (variant.image && this.image) this.image.src = variant.image;
          }

          const unavailable = !variant?.available;
          this.button.dataset.wasDisabled = unavailable ? 'true' : 'false';
          this.button.disabled = unavailable;
          this.button.querySelector('[data-button-label]').textContent = unavailable ? 'Sold out' : 'Add free gift';
        }

        async addGift(event) {
          event.preventDefault();
          if (!this.variantInput?.value || this.button.disabled || giftRequestPending) return;

          const formData = new FormData();
          formData.append('id', this.variantInput.value);
          formData.append('quantity', '1');
          formData.append('properties[_ll_free_gift]', 'true');
          showError(this);
          giftRequestPending = true;
          document.querySelectorAll('cart-gift-picker').forEach((picker) => setBusy(picker, true));

          try {
            await updateCart(window.routes?.cart_add_url || '/cart/add', formData);
            giftRequestPending = false;
          } catch (error) {
            giftRequestPending = false;
            showError(this, error.message);
            document.querySelectorAll('cart-gift-picker').forEach((picker) => setBusy(picker, false));
          }
        }
      },
    );
  }

  if (!customElements.get('cart-shipping-protection')) {
    customElements.define(
      'cart-shipping-protection',
      class CartShippingProtection extends HTMLElement {
        connectedCallback() {
          if (this.dataset.initialized === 'true') return;
          this.dataset.initialized = 'true';
          this.checkbox = this.querySelector('input[type="checkbox"]');
          this.checkbox?.addEventListener('change', () => this.updateProtection());
        }

        async updateProtection() {
          if (!this.checkbox) return;

          const shouldAdd = this.checkbox.checked;
          const formData = new FormData();

          if (shouldAdd) {
            formData.append('id', this.dataset.variantId);
            formData.append('quantity', '1');
            formData.append('properties[_ll_shipping_protection]', 'true');
          } else {
            formData.append('id', this.dataset.lineKey);
            formData.append('quantity', '0');
          }

          showError(this);
          setBusy(this, true);

          try {
            await updateCart(
              shouldAdd ? window.routes?.cart_add_url || '/cart/add' : window.routes?.cart_change_url || '/cart/change',
              formData,
            );
          } catch (error) {
            this.checkbox.checked = !shouldAdd;
            showError(this, error.message);
            setBusy(this, false);
          }
        }
      },
    );
  }

  if (!customElements.get('cart-upsell')) {
    customElements.define(
      'cart-upsell',
      class CartUpsell extends HTMLElement {
        connectedCallback() {
          if (this.dataset.initialized === 'true') return;
          this.dataset.initialized = 'true';
          this.track = this.querySelector('[data-upsell-track]');
          this.cards = [...this.querySelectorAll('[data-upsell-card]')];
          this.position = this.querySelector('[data-upsell-position]');
          this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.prevButton = this.querySelector('[data-upsell-prev]');
          this.nextButton = this.querySelector('[data-upsell-next]');

          this.prevButton?.addEventListener('click', () => this.move(-1));
          this.nextButton?.addEventListener('click', () => this.move(1));
          this.track?.addEventListener('scroll', () => this.queuePositionUpdate(), { passive: true });

          this.cards.forEach((card) => {
            const select = card.querySelector('[data-upsell-variant]');
            select?.addEventListener('change', () => this.updateVariant(card));
            card.querySelector('[data-upsell-add]')?.addEventListener('click', () => this.addProduct(card));
            this.updateVariant(card);
          });

          this.updatePosition();
        }

        move(direction) {
          if (!this.track || !this.cards.length) return;
          const current = this.currentIndex();
          const next = Math.max(0, Math.min(this.cards.length - 1, current + direction));
          const firstOffset = this.cards[0].offsetLeft;
          this.track.scrollTo({
            left: this.cards[next].offsetLeft - firstOffset,
            behavior: this.reducedMotion ? 'auto' : 'smooth',
          });
        }

        currentIndex() {
          if (!this.track || !this.cards.length) return 0;
          const firstOffset = this.cards[0].offsetLeft;
          return this.cards.reduce(
            (best, card, index) => {
              const distance = Math.abs(card.offsetLeft - firstOffset - this.track.scrollLeft);
              return distance < best.distance ? { index, distance } : best;
            },
            { index: 0, distance: Number.POSITIVE_INFINITY },
          ).index;
        }

        queuePositionUpdate() {
          if (this.positionFrame) return;
          this.positionFrame = requestAnimationFrame(() => {
            this.positionFrame = null;
            this.updatePosition();
          });
        }

        updatePosition() {
          const current = this.currentIndex();
          if (this.position) this.position.textContent = `${current + 1} / ${this.cards.length}`;
          if (this.prevButton) this.prevButton.disabled = current === 0;
          if (this.nextButton) this.nextButton.disabled = current === this.cards.length - 1;
        }

        updateVariant(card) {
          const select = card.querySelector('[data-upsell-variant]');
          const option = select?.selectedOptions[0];
          const button = card.querySelector('[data-upsell-add]');
          if (!option || !button) return;

          const available = option.dataset.available === 'true';
          button.dataset.wasDisabled = available ? 'false' : 'true';
          button.disabled = !available;
          button.textContent = available ? 'Add' : 'Sold out';

          const image = card.querySelector('[data-upsell-image]');
          if (image && option.dataset.image) image.src = option.dataset.image;

          const price = card.querySelector('[data-upsell-price]');
          if (price) price.textContent = option.dataset.price || '';

          const compare = card.querySelector('[data-upsell-compare]');
          if (compare) {
            compare.textContent = option.dataset.compare || '';
            compare.hidden = option.dataset.onSale !== 'true';
          }
        }

        async addProduct(card) {
          const select = card.querySelector('[data-upsell-variant]');
          const button = card.querySelector('[data-upsell-add]');
          if (!select?.value || !button || button.disabled) return;

          const formData = new FormData();
          formData.append('id', select.value);
          formData.append('quantity', '1');
          showError(card);
          setBusy(card, true);

          try {
            await updateCart(window.routes?.cart_add_url || '/cart/add', formData);
          } catch (error) {
            showError(card, error.message);
            setBusy(card, false);
            this.updateVariant(card);
          }
        }
      },
    );
  }

  if (!customElements.get('cart-extra-remove')) {
    customElements.define(
      'cart-extra-remove',
      class CartExtraRemove extends HTMLElement {
        connectedCallback() {
          if (this.dataset.initialized === 'true') return;
          this.dataset.initialized = 'true';
          this.querySelector('button')?.addEventListener('click', () => this.removeLine());
        }

        async removeLine() {
          const formData = new FormData();
          formData.append('id', this.dataset.lineKey);
          formData.append('quantity', '0');
          setBusy(this, true);

          try {
            await updateCart(window.routes?.cart_change_url || '/cart/change', formData);
          } catch (error) {
            showError(this, error.message);
            setBusy(this, false);
          }
        }
      },
    );
  }

  if (!customElements.get('cart-extra-cleanup')) {
    customElements.define(
      'cart-extra-cleanup',
      class CartExtraCleanup extends HTMLElement {
        connectedCallback() {
          if (this.dataset.started === 'true' || !this.dataset.lineKey) return;
          this.dataset.started = 'true';
          queueMicrotask(() => this.removeInvalidLine());
        }

        async removeInvalidLine() {
          const formData = new FormData();
          formData.append('id', this.dataset.lineKey);
          formData.append('quantity', '0');

          try {
            await updateCart(window.routes?.cart_change_url || '/cart/change', formData);
          } catch (error) {
            console.error('[Cart drawer extras]', error);
          }
        }
      },
    );
  }
})();
